import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLiveChannel } from '../../hooks/useLiveChannel';
import { validateApiResponse } from '../../lib/validateApiResponse';

/**
 * Ziele — die Projektverwaltung eines Sales-Partners.
 *
 * WARUM EIN EIGENES BOARD UND NICHT RAFAELS FOKUS-BOARD
 * Rafaels Board (FocusPanel, /api/partner/planning) hat denselben Aufbau, den
 * ein Partner braucht: Ziel → Schritt → Haken. Es liegt aber pro Server, und
 * auf dem Partner-Host waere es EIN Board fuer ALLE Partner — jeder saehe die
 * Ziele der anderen und koennte sie aendern.
 *
 * WARUM DIE ZEITSCHIENE DIE ERSTE ANSICHT IST
 * Die erste Fassung war eine Liste in drei Wellen. Sie beantwortete "was ist zu
 * tun", aber nicht "wo stehen wir" — genau das grosse Bild ging verloren
 * (Rafael, 02.09.). Also zuerst die Landkarte: Monatsspalten von September bis
 * Dezember, drei Bahnen (du / gemeinsam / Rafael), jedes Ziel an seinem Monat,
 * und eine Markierung, wo heute liegt. Wer sie ansieht, weiss in drei Sekunden,
 * was hinter ihm liegt, was jetzt laeuft und was noch kommt. Die Liste mit den
 * Schritten bleibt vollstaendig — sie ist der zweite Reiter, und ein Klick auf
 * eine Karte springt genau dorthin.
 */

const API = '/api/partner';

interface Schritt {
  id: string;
  text: string;
  done: boolean;
  doneVon?: string;
}

interface Meilenstein {
  id: string;
  titel: string;
  wann: string;
  ab: string;
  beschreibung?: string;
  wer: string;
  done: boolean;
  doneVon?: string;
  schritte: Schritt[];
}

type Ansicht = 'zeitschiene' | 'liste';

/** Die drei Wellen der Listenansicht. Wie im Fokus-Board, ohne Quartal. */
type Welle = 'jetzt' | 'monat' | 'spaeter';

const WELLEN: { key: Welle; label: string; erklaerung: string }[] = [
  { key: 'jetzt', label: 'Jetzt dran', erklaerung: 'Läuft gerade oder ist überfällig' },
  { key: 'monat', label: 'Diesen Monat', erklaerung: 'Kommt in den nächsten Wochen' },
  { key: 'spaeter', label: 'Später', erklaerung: 'Steht fest, ist aber noch nicht dran' },
];

function welleVon(m: Meilenstein, heute: string): Welle {
  if (m.ab <= heute) return 'jetzt';
  const monatsEnde = `${heute.slice(0, 7)}-31`;
  return m.ab <= monatsEnde ? 'monat' : 'spaeter';
}

const MONATE = ['Jän', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/** "2026-10-01" → "2026-10" */
function monatVon(iso: string): string {
  return iso.slice(0, 7);
}

function monatsLabel(key: string): string {
  const [j, m] = key.split('-');
  return `${MONATE[Number(m) - 1]} ${j.slice(2)}`;
}

/** Alle Monate von der ersten bis zur letzten Spalte — auch die ohne Ziel. */
function monatsSpalten(keys: string[]): string[] {
  if (keys.length === 0) return [];
  const zahl = (k: string) => Number(k.slice(0, 4)) * 12 + Number(k.slice(5, 7)) - 1;
  const lo = Math.min(...keys.map(zahl));
  const hi = Math.max(...keys.map(zahl));
  const out: string[] = [];
  for (let i = lo; i <= hi; i++) {
    out.push(`${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`);
  }
  return out;
}

const FARBE = {
  fertig: 'var(--tnf-9ece6a)',
  ueberfaellig: 'var(--tnf-f7768e)',
  jetzt: 'var(--tnf-7aa2f7)',
  spaeter: 'var(--tnf-565f89)',
};

export default function SalesZielePanel() {
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const isAdmin = user?.role === 'admin';

  const [meilensteine, setMeilensteine] = useState<Meilenstein[]>([]);
  const [fehler, setFehler] = useState('');
  const [laedt, setLaedt] = useState(true);
  const [ansicht, setAnsicht] = useState<Ansicht>('zeitschiene');
  const [nurMeine, setNurMeine] = useState(false);
  const [hervorgehoben, setHervorgehoben] = useState<string | null>(null);
  const [neuerSchritt, setNeuerSchritt] = useState<{ msId: string; text: string } | null>(null);
  const listeRef = useRef<HTMLDivElement>(null);

  const heute = new Date().toISOString().slice(0, 10);
  const heuteMonat = heute.slice(0, 7);

  const laden = useCallback(async () => {
    try {
      const res = await fetch(`${API}/sales/meilensteine`, { signal: AbortSignal.timeout(15000) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      const list = (raw as { meilensteine?: unknown[] }).meilensteine;
      if (!Array.isArray(list)) throw new Error('API /api/partner/sales/meilensteine: erwartet { meilensteine: [] }');
      setMeilensteine(list.map(item =>
        validateApiResponse<Meilenstein>(item, '/api/partner/sales/meilensteine[item]', {
          id: 'string', titel: 'string', wann: 'string', ab: 'string', wer: 'string', done: 'boolean',
          schritte: 'array',
          beschreibung: { type: 'string', optional: true },
          doneVon: { type: 'string', optional: true },
        }),
      ));
      setFehler('');
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  useLiveChannel({
    label: 'SalesZiele',
    onResync: laden,
    onMessage: (msg: { type?: string }) => {
      if (msg.type === 'partner-sales-meilenstein') laden();
    },
  });

  const hakenZiel = useCallback(async (id: string, done: boolean) => {
    setMeilensteine(prev => prev.map(m => (m.id === id ? { ...m, done } : m)));
    try {
      const res = await fetch(`${API}/sales/meilensteine/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ done }),
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await laden();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
      laden();
    }
  }, [laden]);

  const hakenSchritt = useCallback(async (msId: string, schrittId: string, done: boolean) => {
    setMeilensteine(prev => prev.map(m => (m.id === msId
      ? { ...m, schritte: m.schritte.map(s => (s.id === schrittId ? { ...s, done } : s)) }
      : m)));
    try {
      const res = await fetch(`${API}/sales/meilensteine/${msId}/schritte/${schrittId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ done }),
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await laden();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
      laden();
    }
  }, [laden]);

  const schrittAnlegen = useCallback(async (msId: string, text: string) => {
    if (!text.trim()) return;
    try {
      const res = await fetch(`${API}/sales/meilensteine/${msId}/schritte`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNeuerSchritt(null);
      await laden();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    }
  }, [laden]);

  /** Ein Klick auf eine Karte fuehrt dorthin, wo man daran arbeitet. */
  const zurListe = useCallback((id: string) => {
    setAnsicht('liste');
    setHervorgehoben(id);
    window.setTimeout(() => {
      listeRef.current?.querySelector(`[data-ai-id="sales-ziel-${id}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  }, []);

  const meinesVon = useCallback(
    (m: Meilenstein) => m.wer === 'markus' || m.wer === userId,
    [userId],
  );

  const sichtbar = useMemo(
    () => meilensteine.filter(m => !nurMeine || meinesVon(m) || m.wer === 'beide'),
    [meilensteine, nurMeine, meinesVon],
  );

  const gesamt = useMemo(() => {
    const schritte = meilensteine.flatMap(m => m.schritte);
    return {
      ziele: meilensteine.length,
      zieleFertig: meilensteine.filter(m => m.done).length,
      schritte: schritte.length,
      schritteFertig: schritte.filter(s => s.done).length,
    };
  }, [meilensteine]);

  const spalten = useMemo(
    () => monatsSpalten([...sichtbar.map(m => monatVon(m.ab)), heuteMonat]),
    [sichtbar, heuteMonat],
  );

  /**
   * Drei Bahnen statt Themen: fuer einen Vertriebspartner ist die wichtigste
   * Unterscheidung nicht das Produkt, sondern wer zieht — was liegt bei mir,
   * was machen wir zusammen, worauf warte ich.
   */
  const bahnen = useMemo(() => ([
    { key: 'du', label: isAdmin ? 'Markus' : 'Du', filter: (m: Meilenstein) => meinesVon(m) },
    { key: 'beide', label: 'Gemeinsam', filter: (m: Meilenstein) => m.wer === 'beide' },
    { key: 'rafael', label: 'Rafael', filter: (m: Meilenstein) => m.wer === 'rafael' },
  ]), [isAdmin, meinesVon]);

  const farbeVon = useCallback((m: Meilenstein): string => {
    if (m.done) return FARBE.fertig;
    const mon = monatVon(m.ab);
    if (mon < heuteMonat) return FARBE.ueberfaellig;
    if (mon === heuteMonat) return FARBE.jetzt;
    return FARBE.spaeter;
  }, [heuteMonat]);

  if (laedt) {
    return <div style={{ padding: 20, color: 'var(--tn-text-muted)' }}>Lädt…</div>;
  }

  const SPALTE = 168;

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '18px 22px' }} data-ai-id="sales-ziele-panel">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 6, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 17, color: 'var(--tn-text)' }}>Ziele</h2>
        <span style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>
          {gesamt.zieleFertig} von {gesamt.ziele} Zielen · {gesamt.schritteFertig} von {gesamt.schritte} Schritten
        </span>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          {(['zeitschiene', 'liste'] as Ansicht[]).map(a => (
            <button
              key={a}
              data-ai-id={`sales-ziele-ansicht-${a}`}
              onClick={() => setAnsicht(a)}
              style={{
                padding: '4px 11px', fontSize: 12, borderRadius: 5, cursor: 'pointer',
                background: ansicht === a ? 'var(--tn-blue)' : 'var(--tn-surface)',
                color: ansicht === a ? 'var(--tnf-1a1b26)' : 'var(--tn-text-muted)',
                border: '1px solid var(--tn-border)', fontWeight: ansicht === a ? 600 : 400,
              }}
            >
              {a === 'zeitschiene' ? 'Zeitschiene' : 'Alle Schritte'}
            </button>
          ))}
          <label style={{ fontSize: 12, color: 'var(--tn-text-muted)', cursor: 'pointer', marginLeft: 6 }}>
            <input
              type="checkbox"
              data-ai-id="sales-ziele-nur-meine"
              checked={nurMeine}
              onChange={e => setNurMeine(e.target.checked)}
              style={{ marginRight: 6, cursor: 'pointer' }}
            />
            nur was mich betrifft
          </label>
        </div>
      </div>

      {fehler && <div style={{ fontSize: 12, color: 'var(--tnf-f7768e)', marginBottom: 12 }}>{fehler}</div>}

      {ansicht === 'zeitschiene' && (
        <>
          <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginBottom: 16, lineHeight: 1.6 }}>
            Wo wir stehen: jedes Ziel an seinem Monat, in der Bahn dessen, der es zieht. Ein Klick auf
            eine Karte führt zu ihren Schritten.
          </div>

          <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
            <div style={{ minWidth: 90 + spalten.length * SPALTE }}>
              {/* Monatsleiste */}
              <div style={{ display: 'flex', marginBottom: 8, position: 'sticky', top: 0 }}>
                <div style={{ width: 90, flexShrink: 0 }} />
                {spalten.map(sp => {
                  const jetzt = sp === heuteMonat;
                  return (
                    <div
                      key={sp}
                      style={{
                        width: SPALTE, flexShrink: 0, padding: '4px 8px', marginRight: 6,
                        borderRadius: 5, textAlign: 'center', fontSize: 12, fontWeight: 700,
                        letterSpacing: 0.3,
                        background: jetzt ? 'rgba(122,162,247,0.14)' : 'transparent',
                        color: jetzt ? 'var(--tn-blue)' : 'var(--tn-text-muted)',
                        border: `1px solid ${jetzt ? 'var(--tn-blue)' : 'transparent'}`,
                      }}
                    >
                      {monatsLabel(sp)}{jetzt && ' · jetzt'}
                    </div>
                  );
                })}
              </div>

              {bahnen.map(bahn => {
                const drin = sichtbar.filter(bahn.filter);
                if (drin.length === 0) return null;
                return (
                  <div key={bahn.key} style={{ display: 'flex', marginBottom: 10 }} data-ai-id={`sales-bahn-${bahn.key}`}>
                    <div style={{
                      width: 90, flexShrink: 0, fontSize: 12, fontWeight: 600, paddingTop: 6,
                      color: bahn.key === 'du' ? 'var(--tn-blue)' : 'var(--tn-text-muted)',
                    }}>
                      {bahn.label}
                    </div>
                    {spalten.map(sp => {
                      const hier = drin.filter(m => monatVon(m.ab) === sp);
                      const jetzt = sp === heuteMonat;
                      return (
                        <div
                          key={sp}
                          style={{
                            width: SPALTE, flexShrink: 0, marginRight: 6, minHeight: 44,
                            background: jetzt ? 'rgba(122,162,247,0.06)' : 'transparent',
                            borderRadius: 5,
                          }}
                        >
                          {hier.map(m => {
                            const farbe = farbeVon(m);
                            const fertig = m.schritte.filter(s => s.done).length;
                            return (
                              <div
                                key={m.id}
                                data-ai-id={`sales-karte-${m.id}`}
                                onClick={() => zurListe(m.id)}
                                title={`${m.titel} · ${m.wann}${m.beschreibung ? '\n\n' + m.beschreibung : ''}`}
                                style={{
                                  padding: '7px 9px', marginBottom: 5, borderRadius: 5, cursor: 'pointer',
                                  background: 'var(--tn-surface)',
                                  border: '1px solid var(--tn-border)',
                                  borderLeft: `3px solid ${farbe}`,
                                  opacity: m.done ? 0.65 : 1,
                                }}
                              >
                                <div style={{
                                  fontSize: 12, fontWeight: 600, color: 'var(--tn-text)', lineHeight: 1.35,
                                  textDecoration: m.done ? 'line-through' : 'none',
                                }}>
                                  {m.done ? '✓ ' : ''}{m.titel}
                                </div>
                                <div style={{ display: 'flex', gap: 6, marginTop: 3, fontSize: 11, color: 'var(--tn-text-muted)' }}>
                                  <span>{m.wann}</span>
                                  {m.schritte.length > 0 && (
                                    <span style={{ marginLeft: 'auto', color: farbe }}>
                                      {fertig}/{m.schritte.length}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, marginTop: 14, fontSize: 11, color: 'var(--tn-text-muted)', flexWrap: 'wrap' }}>
            {[
              { f: FARBE.fertig, t: 'erledigt' },
              { f: FARBE.jetzt, t: 'läuft jetzt' },
              { f: FARBE.ueberfaellig, t: 'Termin vorbei, offen' },
              { f: FARBE.spaeter, t: 'kommt noch' },
            ].map(l => (
              <span key={l.t} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: l.f, display: 'inline-block' }} />
                {l.t}
              </span>
            ))}
          </div>
        </>
      )}

      {ansicht === 'liste' && (
        <div ref={listeRef}>
          <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginBottom: 18, lineHeight: 1.6 }}>
            Der gemeinsame Fahrplan. Haken setzen dürfen beide — wer etwas erledigt hat, hakt es ab,
            und der andere sieht es, ohne zu fragen.
          </div>

          {WELLEN.map(welle => {
            const drin = sichtbar.filter(m => welleVon(m, heute) === welle.key);
            if (drin.length === 0) return null;
            return (
              <section key={welle.key} style={{ marginBottom: 26 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
                  <div style={{
                    fontSize: 12, fontWeight: 700, color: 'var(--tn-text-muted)',
                    textTransform: 'uppercase', letterSpacing: 0.4,
                  }}>
                    {welle.label}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tn-text-muted)' }}>{welle.erklaerung}</div>
                </div>

                {drin.map(m => {
                  const meiner = meinesVon(m);
                  const fertig = m.schritte.filter(s => s.done).length;
                  const hell = hervorgehoben === m.id;
                  return (
                    <div
                      key={m.id}
                      data-ai-id={`sales-ziel-${m.id}`}
                      style={{
                        marginBottom: 10, borderRadius: 8, background: 'var(--tn-surface)',
                        border: `1px solid ${hell ? 'var(--tn-blue)' : meiner && !m.done ? 'var(--tn-blue)' : 'var(--tn-border)'}`,
                        boxShadow: hell ? '0 0 0 2px rgba(122,162,247,0.25)' : 'none',
                        padding: '12px 14px', opacity: m.done ? 0.6 : 1,
                      }}
                    >
                      <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                        <input
                          type="checkbox"
                          data-ai-id={`sales-ziel-haken-${m.id}`}
                          checked={m.done}
                          onChange={e => hakenZiel(m.id, e.target.checked)}
                          style={{ marginTop: 3, cursor: 'pointer' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                            <span style={{
                              fontSize: 14, fontWeight: 600, color: 'var(--tn-text)',
                              textDecoration: m.done ? 'line-through' : 'none',
                            }}>
                              {m.titel}
                            </span>
                            <span style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>{m.wann}</span>
                            <span style={{
                              fontSize: 11, padding: '1px 7px', borderRadius: 9,
                              background: 'var(--tn-bg)', color: meiner ? 'var(--tn-blue)' : 'var(--tn-text-muted)',
                              border: '1px solid var(--tn-border)',
                            }}>
                              {m.wer === 'beide' ? 'gemeinsam' : m.wer === 'rafael' ? 'Rafael' : 'du'}
                            </span>
                            {m.schritte.length > 0 && (
                              <span style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginLeft: 'auto' }}>
                                {fertig}/{m.schritte.length}
                              </span>
                            )}
                          </div>

                          {m.beschreibung && (
                            <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginTop: 4, lineHeight: 1.55 }}>
                              {m.beschreibung}
                            </div>
                          )}

                          {m.schritte.length > 0 && (
                            <div style={{ marginTop: 9 }}>
                              {m.schritte.map(s => (
                                <label
                                  key={s.id}
                                  style={{
                                    display: 'flex', gap: 9, alignItems: 'flex-start', padding: '3px 0',
                                    fontSize: 12.5, cursor: 'pointer',
                                    color: s.done ? 'var(--tn-text-muted)' : 'var(--tn-text)',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    data-ai-id={`sales-schritt-haken-${s.id}`}
                                    checked={s.done}
                                    onChange={e => hakenSchritt(m.id, s.id, e.target.checked)}
                                    style={{ marginTop: 2, cursor: 'pointer' }}
                                  />
                                  <span style={{ textDecoration: s.done ? 'line-through' : 'none' }}>{s.text}</span>
                                </label>
                              ))}
                            </div>
                          )}

                          {neuerSchritt?.msId === m.id ? (
                            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                              <input
                                autoFocus
                                data-ai-id={`sales-schritt-neu-${m.id}`}
                                value={neuerSchritt.text}
                                onChange={e => setNeuerSchritt({ msId: m.id, text: e.target.value })}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') schrittAnlegen(m.id, neuerSchritt.text);
                                  if (e.key === 'Escape') setNeuerSchritt(null);
                                }}
                                placeholder="Was gehört noch dazu?"
                                style={{
                                  flex: 1, padding: '5px 9px', fontSize: 12.5, borderRadius: 5,
                                  background: 'var(--tn-bg)', color: 'var(--tn-text)',
                                  border: '1px solid var(--tn-border)',
                                }}
                              />
                              <button
                                onClick={() => schrittAnlegen(m.id, neuerSchritt.text)}
                                style={{
                                  padding: '5px 11px', fontSize: 12, borderRadius: 5, cursor: 'pointer',
                                  background: 'var(--tn-blue)', color: 'var(--tnf-1a1b26)', border: 'none', fontWeight: 600,
                                }}
                              >
                                hinzufügen
                              </button>
                            </div>
                          ) : (
                            (meiner || isAdmin) && !m.done && (
                              <button
                                data-ai-id={`sales-schritt-neu-oeffnen-${m.id}`}
                                onClick={() => setNeuerSchritt({ msId: m.id, text: '' })}
                                style={{
                                  marginTop: 7, padding: 0, fontSize: 12, cursor: 'pointer',
                                  background: 'none', border: 'none', color: 'var(--tn-text-muted)',
                                }}
                              >
                                + Schritt
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </section>
            );
          })}
        </div>
      )}

      {sichtbar.length === 0 && !fehler && (
        <div style={{ fontSize: 13, color: 'var(--tn-text-muted)' }}>Keine Ziele eingetragen.</div>
      )}
    </div>
  );
}
